import React from 'react';
import {
  Maybe,
  UserScheduledSessionFieldsFragment,
  UserScheduledSessionParticipantEntryFieldsFragment,
} from '@graphql/user/generated/graphql.types';
import { TScheduledSessionAnswersQueueEntry } from '@typings/TScheduledSessionAnswersQueueEntry';

export type UserContextValue = {
  organization?: { _id: string; region: string; tenant: string };
  participant?: { _id: string; email: string; name: string };
  scheduledSession?: Maybe<UserScheduledSessionFieldsFragment>;
  scheduledSessionEntry?: Maybe<UserScheduledSessionParticipantEntryFieldsFragment>;
  submittingAnswers: boolean;
  scheduledSessionAnswersQueue: TScheduledSessionAnswersQueueEntry[];

  setOrganization?: React.Dispatch<
    React.SetStateAction<UserContextValue['organization']>
  >;
  setParticipant?: React.Dispatch<
    React.SetStateAction<UserContextValue['participant']>
  >;
  setScheduledSession?: React.Dispatch<
    React.SetStateAction<UserContextValue['scheduledSession']>
  >;
  setScheduledSessionEntry?: React.Dispatch<
    React.SetStateAction<UserContextValue['scheduledSessionEntry']>
  >;
  setSubmittingAnswers?: React.Dispatch<React.SetStateAction<boolean>>;
  setScheduledSessionAnswersQueue?: React.Dispatch<
    React.SetStateAction<TScheduledSessionAnswersQueueEntry[]>
  >;
};

export const UserContext = React.createContext<UserContextValue>({
  participant: undefined,
  organization: undefined,
  scheduledSession: undefined,
  scheduledSessionEntry: undefined,
  submittingAnswers: false,
  scheduledSessionAnswersQueue: [],
  setOrganization: () => {},
  setParticipant: () => {},
  setScheduledSession: () => {},
  setScheduledSessionEntry: () => {},
  setSubmittingAnswers: () => {},
  setScheduledSessionAnswersQueue: () => {},
});

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}): JSX.Element => {
  const [organization, setOrganization] =
    React.useState<UserContextValue['organization']>();
  const [participant, setParticipant] =
    React.useState<UserContextValue['participant']>();
  const [scheduledSession, setScheduledSession] =
    React.useState<UserContextValue['scheduledSession']>();
  const [scheduledSessionEntry, setScheduledSessionEntry] =
    React.useState<UserContextValue['scheduledSessionEntry']>();
  const [submittingAnswers, setSubmittingAnswers] = React.useState(false);
  const [scheduledSessionAnswersQueue, setScheduledSessionAnswersQueue] =
    React.useState<TScheduledSessionAnswersQueueEntry[]>([]);

  return (
    <UserContext.Provider
      value={{
        organization,
        participant,
        scheduledSession,
        scheduledSessionEntry,
        submittingAnswers,
        scheduledSessionAnswersQueue,
        setOrganization,
        setParticipant,
        setScheduledSession,
        setScheduledSessionEntry,
        setSubmittingAnswers,
        setScheduledSessionAnswersQueue,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
